import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { ReactComponent as ContractIcon } from '../../../../assets/icons/contract-icon.svg';
import { PrimaryButton } from '../../../../components/Button/ActionButtons';
import ContractModalContext from '../context/ContractModalContext';
import { ContractState } from '../utils/ContractTypes';

import {
  Footer,
  ButtonsContainer,
} from './styles';
import texts from './texts.json';

const ContractModalFooter = ({
  onSendLink,
  showSendLinkOption,
  isWithoutFooterPadding,
}) => {
  const {
    contractDoc,
    contractState,
    isSaving,
    isValidContract,
    generateContract,
    editContract,
    viewTerms,
    cancelEditContract,
    isPreviewTerms,
    isChallengeContract,
    saveChallengeForLead,
    isSavingChallenge,
    canSaveChallengeForLead,
    isSelectedChallengeSaved,
    isChoosingOffering,
    onboardingLink,
  } = useContext(ContractModalContext);

  const getContractCTAText = () => {
    if (isSaving) {
      return texts.saving;
    }

    return contractDoc ? texts.updateTerms : texts.createLink;
  };

  /*
   * A challenge is sold through its own onboarding link, so the rep hands the lead that offer
   * without a contract existing for them. Saving only records the choice and sending the link is
   * the sale itself, so both belong in either footer state: picking the card lands on the terms,
   * and editing goes back to the configurator. Creating a contract would write one before the lead
   * pays, so these take its place — but only while the challenge is the pick. Back in the grid with
   * nothing chosen, the ordinary contract actions return, because no offering is on the table.
   */
  const renderChallengeActions = () => {
    if (!canSaveChallengeForLead) {
      return null;
    }
    return (
      <>
        {/* Nothing left to save once the lead carries it; picking a different challenge brings it back. */}
        {!isSelectedChallengeSaved && (
          <PrimaryButton
            onClick={saveChallengeForLead}
            disabled={isSavingChallenge}
            size="large"
            variant="light"
            icon={<ContractIcon />}
          >
            {texts.saveChallenge}
          </PrimaryButton>
        )}
        {/* Only once the lead carries the challenge: until then the link has no sale to land on. */}
        {showSendLinkOption && isSelectedChallengeSaved && !!onboardingLink && (
          <PrimaryButton
            onClick={onSendLink}
            size="large"
            variant="dimmed"
            icon={<ContractIcon />}
          >
            {texts.sendLink}
          </PrimaryButton>
        )}
      </>
    );
  };

  const renderFooterContent = () => {
    if (contractState === ContractState.CONFIGURATION) {
      return (
        <ButtonsContainer>
          {/* Nothing to create while the challenge is still loaded and no offering has been picked. */}
          {!canSaveChallengeForLead && !isChoosingOffering && (
            <PrimaryButton
              onClick={generateContract}
              disabled={isSaving || !isValidContract}
              size="large"
              variant="light"
              icon={<ContractIcon />}
            >
              {getContractCTAText()}
            </PrimaryButton>
          )}
          {renderChallengeActions()}
          <PrimaryButton
            onClick={viewTerms}
            size="large"
            variant="dimmed"
          >
            {texts.previewTerms}
          </PrimaryButton>
          {!!contractDoc && (
            <PrimaryButton
              onClick={cancelEditContract}
              size="large"
              variant="dimmed"
            >
              {texts.cancelEdit}
            </PrimaryButton>
          )}
        </ButtonsContainer>
      );
    }

    return (
      <ButtonsContainer>
        {!!contractDoc && !canSaveChallengeForLead && !isPreviewTerms && showSendLinkOption && (
          <PrimaryButton
            onClick={onSendLink}
            disabled={!isValidContract}
            size="large"
            variant="light"
            icon={<ContractIcon />}
          >
            {texts.sendLink}
          </PrimaryButton>
        )}
        {renderChallengeActions()}
        <PrimaryButton
          onClick={editContract}
          size="large"
          variant="dimmed"
        >
          {isChallengeContract ? texts.editContract : texts.editTerms}
        </PrimaryButton>
      </ButtonsContainer>
    );
  };

  return (
    <Footer $isWithoutFooterPadding={isWithoutFooterPadding}>
      {renderFooterContent()}
    </Footer>
  );
};

ContractModalFooter.propTypes = {
  onSendLink: PropTypes.func,
  showSendLinkOption: PropTypes.bool,
  isWithoutFooterPadding: PropTypes.bool,
};

ContractModalFooter.defaultProps = {
  showSendLinkOption: false,
  onSendLink: () => {},
  isWithoutFooterPadding: false,
};

export default ContractModalFooter;
